import AcxExpansion from "components/UI/AcxExpansion";
import React from "react";
import theme, { ThemeColors } from "../../../Theme/AppTheme";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { observer } from "mobx-react";
import { Link, useLocation } from "react-router-dom";
import SideNavigationLink from "Layouts/SideNavigationNew/SideNavigationLink";
import DashboardStore from "../Dashboard/DashboardStore";
import { SignalsDashboardType, SignalsObjectType } from "../SignalsModels";
import { sxClsx } from "utils/styleClsx";
import hexToRGB from "utils/hexToRGB";
import SignalsPageStore from "../Store/SignalsPageStore";
import { useStore } from "utils/useStore";

/**
 * Renders the list of dashboards in the sidebar with their charts as expandable links
 * @param dashboards - The list of dashboards to render, should be DashboardStore instances and should be prefiltered when passed in
 * @param isLoading - Whether the dashboards are still loading
 */
export const SidebarDashboards = observer(
    ({
        dashboards,
        isLoading,
    }: {
        dashboards: DashboardStore[];
        isLoading: boolean;
    }) => {
        const signalsPageStore = useStore(SignalsPageStore);
        const location = useLocation();

        return (
            <>
                {isLoading ? (
                    <AcxLoadingIndicator
                        size={45}
                        alternate="PuffLoader"
                        style={{ paddingTop: theme.spacing(4) }}
                    />
                ) : (
                    dashboards
                        .filter(
                            (dashboard) =>
                                dashboard.dashboardType !==
                                SignalsDashboardType.Hidden,
                        )
                        .map((dashboard, i) => {
                        // If our dashboard is one of the standard homes, we don't need to include the ID in the link
                        const linkPath = dashboard.isStandardHomeDashboard
                            ? `/app/signals/${dashboard.route}`
                            : `/app/signals/${dashboard.route}/${dashboard.id}`;

                        // Dashboard is active if its ID matches the current dashboard ID and we're not on home or discover
                        const isActive =
                            dashboard.id ===
                                signalsPageStore.currentDashboardId &&
                            !location.pathname.includes("home") &&
                            !location.pathname.includes("discover");

                        return (
                            <AcxExpansion
                                expanded={false}
                                headerSx={sxClsx(
                                    {
                                        fontSize: "14px",
                                        fontFamily: "Inter",
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                        color: theme.palette.black.main,
                                        marginBottom: theme.spacing(1),
                                        marginTop: theme.spacing(1),
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    },
                                    isActive && {
                                        borderRadius: "5px",
                                        backgroundColor: hexToRGB(
                                            ThemeColors.blue,
                                            0.1,
                                        ),
                                    },
                                )}
                                header={
                                    <Link
                                        className="pendo-ignore"
                                        to={linkPath}
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                    >
                                        {dashboard.title}
                                    </Link>
                                }
                                key={`side-nav-${i}`}
                                // We only show charts as links in the expansion body
                                body={dashboard.signalsObjects
                                    .filter((object) => {
                                        return (
                                            object.objectType ===
                                            SignalsObjectType.Chart ||
                                            object.objectType ===
                                            SignalsObjectType.Table ||
                                            object.objectType === SignalsObjectType.Unique
                                        );
                                    })
                                    .map((chart, index) => (
                                        <SideNavigationLink
                                            key={`side-nav-item-${chart.title?.toString()}-${index}`}
                                            val={{
                                                link: linkPath + `#${chart.id}`,
                                                title:
                                                    chart.title ||
                                                    "Untitled Chart",
                                                signalsVariant: true,
                                            }}
                                            replaceLinks={{}}
                                        />
                                    ))}
                                bodyStyle={{
                                    paddingRight: theme.spacing(0.5),
                                    paddingLeft: theme.spacing(1.75),
                                }}
                                rootStyle={{
                                    paddingRight: theme.spacing(0.75),
                                    paddingLeft: theme.spacing(0.75),
                                    width: "100%",
                                }}
                                chevronCloseSx={{
                                    marginLeft: "0px",
                                    marginRight: theme.spacing(1.5),
                                }}
                                chevronExpandedSx={{
                                    marginLeft: "0px",
                                    marginRight: theme.spacing(1.5),
                                }}
                                unmountOnExit
                            />
                        );
                    })
                )}
            </>
        );
    },
);
